import {
    DeleteTwoTone,
    EditTwoTone,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import {
    Button,
    Checkbox,
    DatePicker,
    Form,
    Input,
    Modal,
    notification,
    Popconfirm,
    Select,
    Skeleton,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomTable from "../common/Table";
import { TeamMangementContext } from ".";
import moment from "moment";
import { weekDays } from "../../services/util";

const { Option } = Select;
const CheckboxGroup = Checkbox.Group;

function Member() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [formData, setFormData] = useState({ staff_id, page: 0, limit: 10 });
    const [memberData, setMemberData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalTitle, setModalTitle] = useState("Add Team Member");
    const [form] = Form.useForm();
    const { memberFormVisible, setMemberFormVisible } =
        useContext(TeamMangementContext);

    const {
        status: teamStatus,
        data: teamRes,
        refetch: getTeam,
    } = useQuery(
        "team",
        async () => {
            return await HTTP("/teams/getTeams", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: shiftStatus,
        data: shiftRes,
        refetch: getShift,
    } = useQuery(
        "shift",
        async () => {
            return await HTTP("/teams/getShifts", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: memberStatus,
        data: memberRes,
        refetch: getMember,
    } = useQuery(
        "member",
        async () => {
            return await HTTP("/teams/getMembers", { ...formData });
        },
        { enabled: false },
    );

    const columns = [
        { title: "Name", dataIndex: "name", key: "name", sorter: () => {} },
        { title: "Email", dataIndex: "email", key: "email", sorter: () => {} },
        {
            title: "Phone",
            dataIndex: "phone_number",
            key: "phone_number",
            sorter: () => {},
        },
        {
            title: "Team",
            render: (text, record) => {
                const assignedTeam = teamRes?.data.find(
                    (e) => e.id == record.team_id,
                );
                return <p>{assignedTeam?.name}</p>;
            },
        },
        {
            title: "Shift",
            render: (text, record) => {
                const assignedShift = shiftRes?.data.find(
                    (e) => e.id == record.shift_id,
                );
                return <p>{assignedShift?.name}</p>;
            },
        },
        {
            title: "Action",
            render: (text, record) => {
                return (
                    <>
                        <Popconfirm
                            title="Are you sure want to delete?"
                            onConfirm={() => deleteMember(record.id)}
                            icon={
                                <QuestionCircleOutlined
                                    style={{
                                        color: "red",
                                    }}
                                />
                            }
                        >
                            <DeleteTwoTone className="actionIcon actionIconDelete" />
                        </Popconfirm>
                        <EditTwoTone
                            className="actionIcon"
                            id={record.id}
                            onClick={() => openEditForm(record)}
                        />
                    </>
                );
            },
        },
    ];

    async function deleteMember(id) {
        const { success, message } = await HTTP("/teams/deleteMember", {
            staff_id,
            id,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        getMember();
    }

    function openEditForm(record) {
        form.setFieldsValue({
            name: record.name,
            id: record.id,
            email: record.email,
            phone_number: record.phone_number,
            team_id: record.team_id,
            shift_id: record.shift_id,
            working_days: record.working_days,
        });
        setModalTitle("Edit Team Member");
        setMemberFormVisible(true);
    }

    async function handleSubmit(values) {
        let url = "/teams/addMember";
        if (values.id) {
            url = "/teams/updateMember";
        }
        const { success, message } = await HTTP(url, {
            staff_id,
            ...values,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        closeForm();
        getMember();
    }

    function closeForm() {
        setMemberFormVisible(false);
        setModalTitle("Add Team Member");
        form.resetFields();
    }

    useEffect(() => {
        getTeam();
        getShift();
        getMember();
    }, []);

    useEffect(() => {
        getMember();
    }, [formData]);

    useEffect(() => {
        if (memberStatus === "success") {
            const { data, total } = memberRes;
            setMemberData(data);
            setTotalRecords(total);
        }
    }, [memberStatus, memberRes]);

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        setFormData({ ...formData, ...others });
    }

    return (
        <>
            {memberStatus !== "idle" && memberStatus !== "loading" ? (
                <CustomTable
                    columns={columns}
                    tableData={memberData}
                    noOfrecords={totalRecords}
                    handleTableChange={handleTableChange}
                    rowKey="id"
                    isScroll
                />
            ) : (
                <Skeleton active title={false} paragraph={{ rows: 10 }} />
            )}

            <Modal
                open={memberFormVisible}
                title={modalTitle}
                footer=""
                destroyOnClose={true}
                onCancel={closeForm}
            >
                <Form form={form} onFinish={handleSubmit}>
                    <Form.Item name="id" className="hidden">
                        <Input id="id" disabled />
                    </Form.Item>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[
                            { required: true, message: "Please Add Name..!" },
                        ]}
                    >
                        <Input id="name" />
                    </Form.Item>
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { required: true, message: "Please Add Email..!" },
                            {
                                type: "email",
                                message: "Invalid email format!",
                            },
                        ]}
                    >
                        <Input id="email" />
                    </Form.Item>
                    <Form.Item
                        label="Phone"
                        name="phone_number"
                        rules={[
                            { required: true, message: "Please Add Phone..!" },
                        ]}
                    >
                        <Input id="phone_number" />
                    </Form.Item>
                    <Form.Item
                        label="Team"
                        name="team_id"
                        rules={[
                            {
                                required: true,
                                message: "Please Select Team..!",
                            },
                        ]}
                    >
                        <Select id="team_id">
                            {teamStatus === "success" &&
                                teamRes?.data &&
                                teamRes.data.map((e) => (
                                    <Option key={e.id} value={e.id}>
                                        {e.name}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="shift_id"
                        label="Shift"
                        rules={[
                            {
                                required: true,
                                message: "Please Select Shift..!",
                            },
                        ]}
                    >
                        <Select id="shift_id">
                            {shiftStatus === "success" &&
                                shiftRes?.data &&
                                shiftRes.data.map((e) => (
                                    <Option key={e.id} value={e.id}>
                                        {e.name}
                                    </Option>
                                ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Working Days"
                        name="working_days"
                        rules={[
                            {
                                required: true,
                                message: "Please Select Working days..!",
                            },
                        ]}
                    >
                        <CheckboxGroup id="working_days">
                            {weekDays.map((e) => (
                                <Checkbox key={e.key} value={e.key}>
                                    {e.title}
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
export default Member;
